/**
 * Utility functions for QR code generation and handling
 */
import QRCode from 'qrcode';
import { Encoder } from '@nuintun/qrcode';

// Generate a QR code URL for an item
export const generateQRCodeUrl = (itemId, baseUrl = window.location.origin) => {
  return `${baseUrl}/item/${itemId}`;
};

// Parse a QR code URL to extract the item ID
export const parseQRCodeUrl = (url) => {
  const urlObj = new URL(url);
  const pathParts = urlObj.pathname.split('/');
  
  // Extract itemId from path (expected format: /item/:itemId)
  if (pathParts.length >= 3 && pathParts[1] === 'item') {
    return pathParts[2];
  }
  
  return null;
};

/**
 * Generate a QR code as a data URL with custom dimensions and type
 * @param {string} itemId - The ID of the item
 * @param {string} itemName - The name of the item (for error messages)
 * @param {number} [width=300] - Width in pixels
 * @param {number} [height=300] - Height in pixels (if different from width, creates rectangular QR code)
 * @param {string} [type='standard'] - Type of QR code: 'standard' or 'rmqr'
 * @returns {Promise<string>} - Data URL of the QR code
 */
export const generateQRCodeDataUrl = async (itemId, itemName = '', width = 300, height = null, type = 'standard') => {
  try {
    const url = generateQRCodeUrl(itemId);
    const qrWidth = Math.max(50, Math.min(2000, width || 300)); // Clamp between 50-2000px
    const qrHeight = height ? Math.max(50, Math.min(2000, height)) : qrWidth;
    
    if (type === 'rmqr') {
      try {
        // Create a new encoder instance for rMQR
        const encoder = new Encoder();
        
        // Configure rMQR code
        encoder.setEncodingHint(true);
        encoder.setErrorCorrectionLevel('H'); // High error correction
        encoder.setVersion(4); // rMQR version (4 is a good default)
        
        // Encode the URL
        const matrix = encoder.encode(url, {
          mode: 'byte',
          ecc: 'H',
          version: 4,
          mask: -1,
          segment: true
        });
        
        // Create canvas for the QR code
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Calculate module sizes
        const moduleSize = Math.min(
          qrWidth / matrix.width,
          qrHeight / matrix.height
        );
        
        // Set canvas size
        canvas.width = matrix.width * moduleSize;
        canvas.height = matrix.height * moduleSize;
        
        // Fill with white background
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Draw the QR code
        ctx.fillStyle = '#000000';
        for (let y = 0; y < matrix.height; y++) {
          for (let x = 0; x < matrix.width; x++) {
            if (matrix.get(x, y)) {
              ctx.fillRect(
                x * moduleSize,
                y * moduleSize,
                moduleSize,
                moduleSize
              );
            }
          }
        }
        
        return canvas.toDataURL('image/png');
      } catch (error) {
        console.error('Error generating rMQR code:', error);
        // Fall back to standard QR code if rMQR generation fails
        return QRCode.toDataURL(url, {
          width: qrWidth,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          },
          errorCorrectionLevel: 'H',
          type: 'image/png'
        });
      }
    } else {
      // Standard QR code using qrcode library
      const qrDataUrl = await QRCode.toDataURL(url, {
        width: qrWidth,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'H',
        type: 'image/png',
        rendererOpts: {
          quality: 1.0
        }
      });
      
      // If we need to create a non-square QR code, we'll render it to a canvas
      if (qrWidth !== qrHeight) {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = qrWidth;
            canvas.height = qrHeight;
            const ctx = canvas.getContext('2d');
            
            // Fill with white background
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, qrWidth, qrHeight);
            
            // Draw the QR code centered
            const scale = Math.min(qrWidth / img.width, qrHeight / img.height);
            const x = (qrWidth - img.width * scale) / 2;
            const y = (qrHeight - img.height * scale) / 2;
            
            ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
            resolve(canvas.toDataURL('image/png', 1.0));
          };
          img.src = qrDataUrl;
        });
      }
      
      return qrDataUrl;
    }
  } catch (error) {
    console.error(`Error generating ${type.toUpperCase()} code for item ${itemName || itemId}:`, error);
    throw new Error(`Failed to generate ${type.toUpperCase()} code: ${error.message}`);
  }
};

/**
 * Download a QR code as an image file
 * @param {string} dataUrl - The data URL of the QR code
 * @param {string} fileName - The name of the file to download (without extension)
 * @param {number} [width] - Optional width in pixels for the downloaded image
 * @param {number} [height] - Optional height in pixels for the downloaded image
 */
export const downloadQRCode = (dataUrl, fileName = 'qrcode', width, height) => {
  return new Promise((resolve, reject) => {
    try {
      const link = document.createElement('a');
      const img = new Image();
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Use custom dimensions if provided, otherwise use image dimensions
          canvas.width = width || img.width;
          canvas.height = height || img.height;
          
          // Fill with white background
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          // Draw the image centered
          const scale = Math.min(
            canvas.width / img.width, 
            canvas.height / img.height
          );
          
          const x = (canvas.width - img.width * scale) / 2;
          const y = (canvas.height - img.height * scale) / 2;
          
          ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
          
          // Convert to blob and download
          canvas.toBlob((blob) => {
            try {
              const url = URL.createObjectURL(blob);
              link.href = url;
              link.download = `${fileName}.png`;
              document.body.appendChild(link);
              link.click();
              
              // Cleanup
              setTimeout(() => {
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                resolve();
              }, 100);
            } catch (err) {
              reject(new Error('Failed to create download link'));
            }
          }, 'image/png', 1.0);
        } catch (err) {
          reject(new Error('Failed to process image'));
        }
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      img.src = dataUrl;
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Create a print window with QR code
 * @param {Object} item - The item to generate QR code for
 * @param {number} width - Width in pixels
 * @param {number} height - Height in pixels
 * @param {string} unit - Display unit (e.g., 'in')
 */
export const printQRCode = async (item, width, height, unit = 'px') => {
  try {
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      throw new Error('Pop-up blocked! Please allow pop-ups for this site to print QR codes.');
    }
    
    // Generate QR code
    const qrDataUrl = await generateQRCodeDataUrl(item.id, item.name, width, height);
    
    // Create print content
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>QR Code - ${item.name}</title>
        <style>
          @page {
            size: auto;
            margin: 0.5cm;
          }
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            background-color: white;
          }
          .qr-container {
            text-align: center;
            padding: 20px;
            page-break-inside: avoid;
          }
          .qr-code {
            max-width: 100%;
            height: auto;
            border: 1px solid #eee;
          }
          .item-name {
            font-size: 16px;
            font-weight: bold;
            margin: 10px 0 5px;
          }
          .item-details {
            font-size: 12px;
            color: #666;
            margin: 0;
          }
          @media print {
            body {
              padding: 0;
            }
            .no-print {
              display: none;
            }
          }
        </style>
      </head>
      <body>
        <div class="qr-container">
          <img 
            class="qr-code" 
            src="${qrDataUrl}" 
            alt="QR Code for ${item.name}"
            style="width: ${width}px; height: ${height}px;"
          />
          <div class="item-name">${item.name}</div>
          <p class="item-details">ID: ${item.id}</p>
          ${item.category ? `<p class="item-details">${item.category}</p>` : ''}
          <button 
            class="no-print" 
            onclick="window.print()"
            style="margin-top: 15px; padding: 8px 16px; cursor: pointer;"
          >
            Print QR Code
          </button>
        </div>
        <script>
          // Auto-print when the window loads
          window.onload = function() {
            setTimeout(() => {
              window.print();
            }, 500);
          };
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
    
  } catch (error) {
    console.error('Error printing QR code:', error);
    throw error;
  }
};
